import { useId, type JSX } from 'react';

import { Label } from '@/components/ui/label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import type {
  EnvironmentMode,
  LaunchOptionsResponse,
  Platform,
  SourceRef,
} from '@/lib/api/quality-types.js';

export type LaunchEnvironmentSelection =
  | { mode: 'inherit' }
  | { mode: 'default' }
  | { mode: 'none' }
  | { mode: 'named'; name: string };

export type LaunchPlatformSelection = 'inherit' | 'auto' | Platform;

/**
 * Confirms a `SourceRef` names a provider/kind pair this client can build a launch payload for, and
 * carries every field that kind requires. `SourceRef` is asserted through an API-response cast, not
 * validated at runtime (see `quality-types.ts`), so a newer service version or a malformed value can
 * carry a `coordinates.kind` outside the kinds this union declares. A `kind === 'x' ? … : …` ternary
 * treats any such value as the other branch and would launch a run, cycle, suite, plan or set the
 * caller never asked for — every caller that builds a launch payload from a `SourceRef` must reject an
 * unsupported kind here first, rather than let a ternary guess.
 */
export function isSupportedSourceRef(sourceRef: SourceRef): boolean {
  switch (sourceRef.provider) {
    case 'testrail':
      if (sourceRef.coordinates.kind === 'run') {
        return Boolean(
          sourceRef.coordinates.externalProjectId &&
          sourceRef.coordinates.runId,
        );
      }
      if (sourceRef.coordinates.kind === 'suite') {
        return Boolean(
          sourceRef.coordinates.externalProjectId &&
          sourceRef.coordinates.suiteId,
        );
      }
      return false;
    case 'zephyr':
      if (sourceRef.coordinates.kind === 'cycle') {
        return Boolean(
          sourceRef.coordinates.externalProjectId &&
          sourceRef.coordinates.cycleKey,
        );
      }
      if (sourceRef.coordinates.kind === 'folder') {
        return Boolean(
          sourceRef.coordinates.externalProjectId &&
          sourceRef.coordinates.folderId,
        );
      }
      return false;
    case 'azure':
      return (
        sourceRef.coordinates.kind === 'suite' &&
        Boolean(
          sourceRef.coordinates.externalProjectId &&
          sourceRef.coordinates.planId &&
          sourceRef.coordinates.suiteId,
        )
      );
    case 'xray':
      return (
        (sourceRef.coordinates.kind === 'testPlan' ||
          sourceRef.coordinates.kind === 'testSet') &&
        Boolean(
          sourceRef.coordinates.externalProjectId &&
          sourceRef.coordinates.containerId,
        )
      );
    case 'own-tms':
      return (
        sourceRef.coordinates.kind === 'suite' &&
        Boolean(
          sourceRef.coordinates.externalProjectId &&
          sourceRef.coordinates.suiteId,
        )
      );
    default:
      return false;
  }
}

/** The source link a reference resolves through; a suite stored in Quality has none. */
export function sourceLinkIdOf(sourceRef: SourceRef): string | null {
  return sourceRef.provider === 'own-tms' ? null : sourceRef.sourceLinkId;
}

/** Human-readable authorized target identity for disambiguating same-named provider containers. */
export function sourceRefLabel(sourceRef: SourceRef): string {
  if (sourceRef.provider === 'own-tms') return 'Stored in Quality';
  const prefix = `${sourceRef.provider === 'azure' ? 'Azure DevOps' : sourceRef.provider === 'xray' ? 'Xray Cloud' : sourceRef.provider === 'zephyr' ? 'Zephyr' : 'TestRail'} project ${sourceRef.coordinates.externalProjectId}`;
  const binding = `source link ${sourceRef.sourceLinkId}`;
  if (sourceRef.provider === 'azure') {
    return `${prefix} · plan ${sourceRef.coordinates.planId} · suite ${sourceRef.coordinates.suiteId} · ${binding}`;
  }
  if (sourceRef.provider === 'testrail') {
    const container =
      sourceRef.coordinates.kind === 'run'
        ? `run ${sourceRef.coordinates.runId}`
        : `suite ${sourceRef.coordinates.suiteId}`;
    return `${prefix} · ${container} · ${binding}`;
  }
  if (sourceRef.provider === 'zephyr') {
    const container =
      sourceRef.coordinates.kind === 'cycle'
        ? `cycle ${sourceRef.coordinates.cycleKey}`
        : `folder ${sourceRef.coordinates.folderId}`;
    return `${prefix} · ${container} · ${binding}`;
  }
  return `${prefix} · ${sourceRef.coordinates.kind === 'testPlan' ? 'test plan' : 'test set'} ${sourceRef.coordinates.containerId} · ${binding}`;
}

/** Converts the user's explicit environment intent to the canonical request fields. */
export function environmentSelectionBody(
  selection: LaunchEnvironmentSelection,
): { environmentMode?: EnvironmentMode; environmentName?: string } {
  if (selection.mode === 'inherit') return {};
  if (selection.mode === 'named') {
    return { environmentMode: 'named', environmentName: selection.name };
  }
  return { environmentMode: selection.mode };
}

/** Converts the target selector without confusing launch Auto with relaunch Auto. */
export function platformSelectionBody(
  selection: LaunchPlatformSelection,
  relaunch: false,
): { targetPlatforms?: Platform[] };
export function platformSelectionBody(
  selection: LaunchPlatformSelection,
  relaunch: true,
): { targetPlatforms?: Platform[] | null };
export function platformSelectionBody(
  selection: LaunchPlatformSelection,
  relaunch: boolean,
): { targetPlatforms?: Platform[] | null } {
  if (selection === 'inherit') return {};
  if (selection === 'auto') {
    return relaunch ? { targetPlatforms: null } : {};
  }
  return { targetPlatforms: [selection] };
}

function environmentValue(selection: LaunchEnvironmentSelection): string {
  return selection.mode === 'named'
    ? `named:${selection.name}`
    : selection.mode;
}

export function LaunchEnvironmentSelect({
  launchOptions,
  value,
  onChange,
  inheritedName,
  className,
}: {
  launchOptions: LaunchOptionsResponse;
  value: LaunchEnvironmentSelection;
  onChange: (value: LaunchEnvironmentSelection) => void;
  inheritedName?: string | null;
  className?: string;
}): JSX.Element {
  const selectId = useId();
  const namedOptions = launchOptions.environments.options.filter(
    (option, index, options) =>
      options.findIndex(
        (candidate) =>
          candidate.name.toLowerCase() === option.name.toLowerCase(),
      ) === index,
  );
  const selectedNamed =
    value.mode === 'named'
      ? namedOptions.find(
          (option) => option.name.toLowerCase() === value.name.toLowerCase(),
        )
      : undefined;
  const selectedReasons =
    value.mode === 'default'
      ? launchOptions.environments.reasons
      : (selectedNamed?.reasons ?? []);

  return (
    <div className={className ?? 'space-y-1.5'}>
      <Label htmlFor={selectId}>Environment</Label>
      <Select
        value={environmentValue(value)}
        onValueChange={(next) => {
          if (next === 'inherit') onChange({ mode: 'inherit' });
          if (next === 'default') onChange({ mode: 'default' });
          if (next === 'none') onChange({ mode: 'none' });
          if (next.startsWith('named:')) {
            const name = next.slice('named:'.length);
            const option = namedOptions.find(
              (candidate) => candidate.name === name,
            );
            if (option?.status === 'available') {
              onChange({ mode: 'named', name: option.name });
            }
          }
        }}
      >
        <SelectTrigger id={selectId} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {inheritedName !== undefined ? (
            <SelectItem value="inherit">
              {inheritedName
                ? `Inherit (${inheritedName})`
                : 'Inherit (no environment)'}
            </SelectItem>
          ) : null}
          <SelectItem
            value="default"
            disabled={
              launchOptions.environments.default_status === 'unavailable'
            }
          >
            {launchOptions.environments.default_name
              ? `Default (${launchOptions.environments.default_name})`
              : 'Default (no environment configured)'}
          </SelectItem>
          <SelectItem value="none">None</SelectItem>
          {namedOptions.map((option) => (
            <SelectItem
              key={`${option.name}-${option.status}`}
              value={`named:${option.name}`}
              disabled={option.status === 'unavailable'}
            >
              {option.name}
              {option.status === 'unavailable' ? ' (unavailable)' : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selectedReasons.map((reason) => (
        <p
          key={`${reason.code}-${reason.message}`}
          className="text-xs text-destructive"
        >
          {reason.message}
        </p>
      ))}
    </div>
  );
}

export function LaunchPlatformSelect({
  launchOptions,
  value,
  onChange,
  inheritedPlatforms,
  className,
}: {
  launchOptions: LaunchOptionsResponse;
  value: LaunchPlatformSelection;
  onChange: (value: LaunchPlatformSelection) => void;
  inheritedPlatforms?: Platform[] | null;
  className?: string;
}): JSX.Element {
  const selectId = useId();
  const selectedOption = launchOptions.target_platforms.find(
    (option) => option.platform === value,
  );
  const inheritedLabel = inheritedPlatforms?.length
    ? inheritedPlatforms
        .map((platform) => (platform === 'web' ? 'Web' : 'Mobile'))
        .join(', ')
    : 'Auto';

  return (
    <div className={className ?? 'space-y-1.5'}>
      <Label htmlFor={selectId}>Target platform</Label>
      <Select
        value={value}
        onValueChange={(next) => onChange(next as LaunchPlatformSelection)}
      >
        <SelectTrigger id={selectId} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {inheritedPlatforms !== undefined ? (
            <SelectItem value="inherit">Inherit ({inheritedLabel})</SelectItem>
          ) : null}
          <SelectItem value="auto">Auto</SelectItem>
          {launchOptions.target_platforms.map((option) => (
            <SelectItem
              key={option.platform}
              value={option.platform}
              disabled={option.status === 'unavailable'}
            >
              {option.platform === 'web' ? 'Web' : 'Mobile'}
              {option.status === 'unavailable' ? ' (unavailable)' : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selectedOption?.reasons.map((reason) => (
        <p
          key={`${reason.code}-${reason.message}`}
          className="text-xs text-muted-foreground"
        >
          {reason.message}
        </p>
      ))}
    </div>
  );
}
